import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "../lib/authHeaders";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import { Brain, FileText, Upload, MessageCircle, Stethoscope, FolderPlus, Search } from "lucide-react";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

// Build headers for chat requests — JWT Bearer if logged in, X-Beta-Access-Code if via 1111
function getChatHeaders() {
  const auth = getAuthHeaders();
  if (auth.Authorization) return auth;
  try {
    const beta = JSON.parse(localStorage.getItem("beta_access") || "{}");
    if (beta.granted) return { "X-Beta-Access-Code": "1111" };
  } catch {}
  return {};
}

const mdComponents = {
  a: ({href, children}) => {
    const pmidMatch = href && href.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/);
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 underline">{children}{pmidMatch ? ` (PMID: ${pmidMatch[1]})` : ''}</a>;
  },
  h1: ({children}) => <h2 className="text-lg font-bold text-teal-800 mt-3 mb-2 border-b pb-1">{children}</h2>,
  h2: ({children}) => <h3 className="text-base font-semibold text-teal-700 mt-2 mb-1">{children}</h3>,
  h3: ({children}) => <h4 className="text-sm font-semibold text-gray-800 mt-2 mb-1">{children}</h4>,
  hr: () => <hr className="my-2 border-gray-200" />,
  ul: ({children}) => <ul className="list-disc pl-5 space-y-0.5 my-1">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal pl-5 space-y-0.5 my-1">{children}</ol>,
  li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
  p: ({children}) => <p className="text-sm leading-relaxed mb-1.5">{children}</p>,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
  table: ({node, children, ...props}) => <div className="overflow-x-auto my-2"><table className="min-w-full text-sm border-collapse border border-gray-300">{children}</table></div>,
  thead: ({node, children, ...props}) => <thead className="bg-teal-50">{children}</thead>,
  th: ({node, children, ...props}) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold text-teal-800 text-xs">{children}</th>,
  td: ({node, children, ...props}) => <td className="border border-gray-300 px-2 py-1 text-xs">{children}</td>,
  code: ({children, className}) => className ? <code className="block bg-gray-100 rounded p-2 text-xs overflow-x-auto my-1">{children}</code> : <code className="bg-gray-100 rounded px-1 text-xs">{children}</code>,
  blockquote: ({children}) => <blockquote className="border-l-3 border-teal-400 pl-3 italic text-gray-600 my-2">{children}</blockquote>,
};

const DrPeptideChat = ({ setCurrentView }) => {
  // All chat state is LOCAL to this component - prevents parent re-renders from breaking inputs
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [patientContext, setPatientContext] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedLabFiles, setUploadedLabFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showPatientInfo, setShowPatientInfo] = useState(true);
  const [extractionPreview, setExtractionPreview] = useState(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [patientSummary, setPatientSummary] = useState(null);
  const [showPatientSummary, setShowPatientSummary] = useState(false);
  const [generatingPatientSummary, setGeneratingPatientSummary] = useState(false);

  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [createPatientName, setCreatePatientName] = useState('');
  const [createPatientProtocol, setCreatePatientProtocol] = useState('');
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [includeCustomFormulations, setIncludeCustomFormulations] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackContext, setFeedbackContext] = useState('');

  const chatInputRef = useRef(null);
  const patientContextRef = useRef(null);

  const removeUploadedFile = (filename) => {
    setUploadedFiles(prev => prev.filter(f => f.filename !== filename));
  };

  const handleFileUpload = async (files, fileType = 'labs') => {
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));
    formData.append('file_type', fileType);

    try {
      setUploadingFiles(true);
      const uploadUrl = `${API}/${fileType}/analyze`;

      // Try upload with retry logic for transient failures
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await axios.post(uploadUrl, formData, {
            timeout: 120000
          });
          if (response.data && response.data.analyses) {
            // Check if any analysis actually has extracted text
            const hasRealData = response.data.analyses.some(a =>
              a.analysis?.extracted_text && a.analysis.extracted_text.length > 50 &&
              !a.analysis.extracted_text.includes('Text extraction returned limited')
            );
            setExtractionPreview({ files: response.data.analyses, rawData: response.data, fileType, hasRealData });
            setShowExtractionModal(true);
            setUploadedFiles(prev => [...prev, ...response.data.analyses.map(a => ({...a, fileType}))]);
            return; // Success - exit
          }
        } catch (apiError) {
          lastError = apiError;
          console.warn(`Upload attempt ${attempt + 1} failed:`, apiError.message);
          if (attempt < 1) await new Promise(r => setTimeout(r, 2000)); // Wait 2s before retry
        }
      }

      // All attempts failed - show fallback
      console.error('File upload failed after retries:', lastError?.message);
      const fallbackAnalyses = Array.from(files).map(file => ({
        filename: file.name, filesize: file.size, filetype: file.type, fileType,
        analysis: {
          extracted_text: fileType === 'genetics'
            ? `Genetic Test: ${file.name}\n\nAnalysis is temporarily unavailable. Please try again or enter genetic markers manually.`
            : `Lab Results: ${file.name}\n\nAnalysis is temporarily unavailable. Please try again or paste lab values into the Patient Information box above.`,
          status: 'pending_manual_entry'
        }
      }));
      setExtractionPreview({ files: fallbackAnalyses, fallback: true, fileType });
      setShowExtractionModal(true);
      setUploadedFiles(prev => [...prev, ...fallbackAnalyses]);
    } catch (error) {
      toast.error(`Error processing ${fileType} files`);
    } finally {
      setUploadingFiles(false);
    }
  };

  const applyExtractedData = () => {
    if (!extractionPreview) return;
    const analysisText = extractionPreview.files.map(file => {
      const analysis = file.analysis || {};
      const text = analysis.extracted_text || analysis.interpretation || JSON.stringify(analysis, null, 2);
      return `\n\n${file.filename}:\n${text}`;
    }).join('\n');
    const timestamp = new Date().toLocaleTimeString();
    setPatientContext(prev => (prev || '') + `\n\nAUTO-ADDED ${timestamp}${analysisText}\n`);
    if (patientContextRef.current) {
      patientContextRef.current.value = (patientContextRef.current.value || '') + `\n\nAUTO-ADDED ${timestamp}${analysisText}\n`;
    }
    setShowExtractionModal(false);
    setExtractionPreview(null);
  };

  const generatePatientSummary = async (clinicalResponse) => {
    setGeneratingPatientSummary(true);
    try {
      const summaryPrompt = `You are a medical communication expert. Convert this clinical practitioner response into a clear, patient-friendly summary that a doctor can print and hand to their patient.\n\nCLINICAL RESPONSE:\n${clinicalResponse}\n\nCreate a PATIENT-FRIENDLY SUMMARY that:\n- Uses simple, everyday language (avoid medical jargon)\n- Explains medical terms when they must be used\n- Focuses on actionable information the patient needs\n- Maintains accuracy but increases accessibility\n- Includes clear next steps\n- Is encouraging and supportive in tone\n- Can be printed on 1-2 pages\n\nFormat with clear headings and bullet points for easy reading.`;
      const response = await axios.post(`${API}/dr-peptide/chat`, { message: summaryPrompt, conversation_history: [] }, { timeout: 60000, headers: getChatHeaders() });
      if (response.data.success) {
        setPatientSummary({ original: clinicalResponse, summary: response.data.response, timestamp: new Date().toLocaleString() });
        setShowPatientSummary(true);
      }
    } catch (error) {
      toast.error('Error generating patient summary');
    } finally {
      setGeneratingPatientSummary(false);
    }
  };

  const chatWithDrPeptide = async (message, retryCount = 0) => {
    if (!message || !message.trim()) return;
    setChatLoading(true);

    if (retryCount === 0) {
      setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    }

    try {
      let enhancedMessage = message;
      if (patientContext && patientContext.trim()) {
        enhancedMessage = `PATIENT INFORMATION:\n${patientContext.trim()}\n\nQUESTION: ${message}`;
      }
      if (uploadedFiles.length > 0) {
        const labContext = uploadedFiles.map(file => {
          const analysis = file.analysis || file;
          return `LAB FILE: ${file.filename || 'uploaded file'}\nANALYSIS: ${JSON.stringify(analysis).substring(0, 500)}`;
        }).join('\n\n');
        enhancedMessage += `\n\nUPLOADED LAB FILES:\n${labContext}`;
      }

      const conversationHistory = chatMessages.slice(-6).map(msg => ({ role: msg.role, content: msg.content }));
      
      // Try streaming first
      try {
        const streamResponse = await fetch(`${API}/dr-peptide/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getChatHeaders() },
          body: JSON.stringify({ message: enhancedMessage, conversation_history: conversationHistory })
        });

        if (streamResponse.ok && streamResponse.headers.get('content-type')?.includes('text/event-stream')) {
          // Add empty assistant message that we'll build up.
          // CRITICAL: only add the bubble on retryCount === 0. On retries, REPLACE the existing
          // last assistant bubble (which was created on the prior attempt and may be empty/error)
          // so we never accumulate multiple empty bubbles per logical query. (Jun 9, 2026 fix.)
          if (retryCount === 0) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: '' }]);
          } else {
            setChatMessages(prev => {
              const updated = [...prev];
              if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
                updated[updated.length - 1] = { role: 'assistant', content: '' };
              } else {
                updated.push({ role: 'assistant', content: '' });
              }
              return updated;
            });
          }
          
          const reader = streamResponse.body.getReader();
          const decoder = new TextDecoder();
          let fullText = '';
          let streamErrorDetail = null;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value, { stream: true });
            const lines = text.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  // Empty-LLM-after-retry surfaced as an SSE error event. Capture
                  // the detail so we can show a retry toast after the stream ends,
                  // instead of rendering a blank/canned bubble. (2026-07-05 hotfix.)
                  if (data.error) {
                    streamErrorDetail = data.error;
                    break;
                  }
                  if (data.chunk) {
                    fullText += data.chunk;
                    setChatMessages(prev => {
                      const updated = [...prev];
                      updated[updated.length - 1] = { role: 'assistant', content: fullText };
                      return updated;
                    });
                  }
                  if (data.done) break;
                } catch {}
              }
            }
            if (streamErrorDetail) break;
          }

          // If the server signalled an empty-LLM error and we streamed nothing
          // usable, surface a retry toast and re-throw so we fall through to the
          // non-streaming request (which may also 500 and be handled below).
          if (streamErrorDetail && !fullText.trim()) {
            throw new Error(streamErrorDetail);
          }
          setChatLoading(false);
          return;
        }
      } catch (streamErr) {
        // Streaming failed, fall back to regular request
        console.warn('Streaming unavailable, falling back:', streamErr.message);
      }

      // Fallback: regular non-streaming request
      const response = await axios.post(`${API}/dr-peptide/chat`, {
        message: enhancedMessage,
        conversation_history: conversationHistory
      }, { timeout: 120000, headers: { 'Content-Type': 'application/json', ...getChatHeaders() } });

      // Non-streaming success / error paths must also REPLACE the last assistant bubble on retry
      // rather than append, to prevent empty-bubble pile-up. (Jun 9, 2026 fix.)
      const _setAssistantContent = (content) => {
        if (retryCount === 0) {
          setChatMessages(prev => [...prev, { role: 'assistant', content }]);
        } else {
          setChatMessages(prev => {
            const updated = [...prev];
            if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
              updated[updated.length - 1] = { role: 'assistant', content };
            } else {
              updated.push({ role: 'assistant', content });
            }
            return updated;
          });
        }
      };

      if (response.data && response.data.success) {
        _setAssistantContent(response.data.response);
      } else {
        _setAssistantContent(response.data?.response || 'I encountered an issue processing your request. Please try again.');
      }
    } catch (error) {
      if (retryCount < 4 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504)) {
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        setTimeout(() => chatWithDrPeptide(message, retryCount + 1), retryDelay);
        return;
      }
      let errorMessage = 'I apologize, but I\'m experiencing technical difficulties right now. Please try again.';
      if (error.code === 'ECONNABORTED') errorMessage = 'Response timeout - please try a shorter question.';
      else if (error.response?.status === 500) errorMessage = 'Server error occurred. Please try again in a moment.';
      else if (error.code === 'ERR_NETWORK') errorMessage = 'Network connection issue. Please check your internet connection.';

      // Empty-LLM-after-retry hotfix (2026-07-05): when Dr. Peptide couldn't
      // produce a response (500 from the endpoint, or the SSE error event), show
      // a small red toast with a Retry button that re-submits the last message.
      // The chat is NOT crashed or blanked — the error bubble below still renders.
      const isEmptyResponseFailure =
        error.response?.status === 500 ||
        (error.response?.data?.detail || '').includes('unable to respond') ||
        (error.message || '').includes('unable to respond');
      if (isEmptyResponseFailure) {
        toast.error("Dr. Peptide didn't respond — please try that again", {
          action: {
            label: 'Retry',
            onClick: () => chatWithDrPeptide(message),
          },
        });
      }
      // Same replace-on-retry semantics for error messages.
      if (retryCount === 0) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
      } else {
        setChatMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
            updated[updated.length - 1] = { role: 'assistant', content: errorMessage };
          } else {
            updated.push({ role: 'assistant', content: errorMessage });
          }
          return updated;
        });
      }
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = () => {
    const inputValue = chatInputRef.current?.value?.trim();
    if (inputValue) {
      let messageWithContext = patientContext
        ? `PATIENT CONTEXT: ${patientContext}\n\nQUESTION: ${inputValue}`
        : inputValue;
      // Append custom formulations preference
      if (includeCustomFormulations) {
        messageWithContext += '\n\n[Custom formulations (M-51, RG-5555, N-5550) are enabled — recommend when clinically appropriate]';
      } else {
        messageWithContext += '\n\n[Custom formulations OFF — recommend only standard individual peptides]';
      }
      chatWithDrPeptide(messageWithContext);
      chatInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 overflow-x-hidden" data-testid="dr-peptide-chat">
      <Card className="min-h-[400px] md:h-[600px] flex flex-col">
        <CardHeader className="px-4 md:px-6">
          <CardTitle className="flex items-center gap-2">
            <div className="flex items-center gap-3">
              <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide - AI peptide therapy assistant" className="h-10 w-10 md:h-12 md:w-12 rounded-full border-2 border-teal-200" />
              <div>
                <h1 className="text-lg md:text-xl font-bold">Dr. Peptide AI</h1>
                <p className="text-xs md:text-sm text-gray-600">Your Functional Medicine Expert</p>
              </div>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col">
          {chatMessages.length > 0 && (
            <div className="mb-2 flex justify-end">
              <Button variant="ghost" size="sm" data-testid="clear-chat-btn"
                onClick={() => {
                  setChatMessages([]);
                  setPatientContext('');
                  if (patientContextRef.current) patientContextRef.current.value = '';
                  setUploadedLabFiles([]);
                  setUploadedFiles([]);
                }}
                className="text-gray-500 hover:text-red-600"
              >
                Clear Chat
              </Button>
            </div>
          )}

          {/* Patient Context Section */}
          {showPatientInfo && (
            <div className="mb-4 p-4 border rounded-lg bg-teal-50 border-teal-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-teal-900 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Patient Information
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowPatientInfo(false)}>X</Button>
              </div>
              <textarea
                ref={patientContextRef}
                className="w-full p-2 border rounded text-sm mb-2 min-h-[100px]"
                placeholder="Enter patient details (age, sex, weight, medical history, current medications, labs, symptoms, etc.)..."
                rows={3}
                defaultValue={patientContext}
                data-testid="patient-context-input"
                onBlur={(e) => setPatientContext(e.target.value)}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-teal-500', 'bg-teal-50'); }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-teal-500', 'bg-teal-50');
                  const files = Array.from(e.dataTransfer.files);
                  if (files.length > 0) {
                    setUploadedLabFiles(prev => [...prev, ...files]);
                    await handleFileUpload(files, 'labs');
                  }
                }}
              />

              <div className="space-y-3">
                <div className="flex gap-2 items-center flex-wrap">
                  <Button variant="outline" size="sm" disabled={uploadingFiles} data-testid="upload-labs-btn"
                    onClick={() => document.getElementById('chat-lab-file-input').click()}
                    className="flex items-center gap-2"
                  >
                    {uploadingFiles ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div> Analyzing...</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Upload Lab Results</>
                    )}
                  </Button>

                  <Button variant="outline" size="sm" disabled={uploadingFiles} data-testid="upload-genetics-btn"
                    onClick={() => document.getElementById('chat-genetics-file-input').click()}
                    className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {uploadingFiles ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div> Analyzing...</>
                    ) : (
                      <>Upload Genetic Tests</>
                    )}
                  </Button>

                  <div className="text-xs text-gray-500 ml-2">Drag & drop files here or use buttons</div>

                  {/* Custom Formulations Toggle */}
                  <div className="flex items-center gap-2 ml-auto" data-testid="chat-custom-formulations-toggle">
                    <span className={`text-xs font-medium ${includeCustomFormulations ? 'text-green-600' : 'text-gray-400'}`}>
                      Custom Formulations: {includeCustomFormulations ? 'ON' : 'OFF'}
                    </span>
                    <Switch checked={includeCustomFormulations} onCheckedChange={setIncludeCustomFormulations}
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300" />
                  </div>

                  <input id="chat-lab-file-input" type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.txt"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) { setUploadedLabFiles(prev => [...prev, ...files]); await handleFileUpload(files, 'labs'); }
                      e.target.value = '';
                    }}
                  />
                  <input id="chat-genetics-file-input" type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.txt,.vcf,.23andme"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) { setUploadedLabFiles(prev => [...prev, ...files]); await handleFileUpload(files, 'genetics'); }
                      e.target.value = '';
                    }}
                  />
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
                    <div className="text-sm font-medium text-green-800">Attached Files ({uploadedFiles.length})</div>
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-white rounded p-2 border border-green-200">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{file.filename}</span>
                          {file.filesize && <span className="text-gray-500">({(file.filesize / 1024).toFixed(1)} KB)</span>}
                        </div>
                        <button onClick={() => removeUploadedFile(file.filename)} className="text-red-600 hover:text-red-800 font-bold">x</button>
                      </div>
                    ))}
                  </div>
                )}

                {patientContext && patientContext.length > 50 && (
                  <Button size="sm" variant="outline" className="ml-2" data-testid="interpret-labs-btn"
                    onClick={async () => {
                      try {
                        setChatLoading(true);
                        const response = await axios.post(`${BACKEND_URL}/api/labs/interpret`, { text: patientContext }, { timeout: 60000 });
                        if (response.data.success) {
                          const analysis = response.data.analysis;
                          const interpretation = response.data.ai_interpretation;
                          let labSummary = `\nLab Analysis Complete\n\nHealth Score: ${analysis.health_score}/100\nTests: ${analysis.total_tests} total, ${analysis.abnormal_count} abnormal, ${analysis.critical_count} critical\n\n`;
                          const abnormal = analysis.results.filter(r => r.status !== 'normal');
                          if (abnormal.length > 0) {
                            labSummary += `Abnormal Results:\n`;
                            abnormal.forEach(r => { labSummary += `${r.flag} ${r.display_name}: ${r.value} ${r.unit} (Normal: ${r.normal_range})\n`; });
                          }
                          if (analysis.recommendations?.length > 0) {
                            labSummary += `\nRecommendations:\n`;
                            analysis.recommendations.forEach(rec => { labSummary += `- ${rec}\n`; });
                          }
                          labSummary += `\nClinical Interpretation:\n${interpretation}`;
                          setChatMessages(prev => [...prev, { role: 'assistant', content: labSummary }]);
                        } else {
                          toast.error('Could not interpret labs');
                        }
                      } catch (err) {
                        toast.error('Lab interpretation failed');
                      } finally {
                        setChatLoading(false);
                      }
                    }}
                  >
                    Interpret Labs
                  </Button>
                )}
              </div>
            </div>
          )}

          {!showPatientInfo && (
            <Button variant="outline" size="sm" className="mb-4" onClick={() => setShowPatientInfo(true)}>
              <FileText className="h-4 w-4 mr-2" /> Show Patient Info
            </Button>
          )}

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-4 border rounded-lg bg-gray-50" data-testid="chat-messages">
            {chatMessages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <div className="mb-4">
                  <p className="text-xs text-slate-400 px-4 py-2 bg-slate-50 rounded inline-block">PeptideProtocols.ai is a clinical decision support tool. All recommendations must be reviewed by the prescribing practitioner. This is not medical advice.</p>
                </div>
                <div className="mt-4">
                  <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide" className="h-24 w-24 mx-auto mb-4 rounded-full border-2 border-teal-200" />
                </div>
                <p>Hello! I'm Dr. Peptide, your AI clinical partner for peptide therapy.</p>
                <p className="text-sm mt-2">I can help you with patient protocols, lab interpretation, and personalized health optimization strategies.</p>
                <p className="text-xs mt-2 text-teal-600">Add patient context above for personalized clinical recommendations</p>
              </div>
            )}

            {chatMessages.map((message, index) => (
              <div key={index} className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${message.role === 'user' ? 'bg-teal-600 text-white' : 'bg-white border shadow-sm'}`}>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide - AI peptide therapy assistant" className="h-5 w-5 rounded-full" />
                      <span className="text-sm font-semibold text-teal-600">Dr. Peptide</span>
                    </div>
                  )}
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none prose-headings:text-teal-800 prose-strong:font-semibold">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {typeof message.content === 'string' ? message.content : String(message.content)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </div>

                {/* Action Buttons for Last Assistant Message */}
                {message.role === 'assistant' && index === chatMessages.length - 1 && !chatLoading && (
                  <div className="flex gap-2 mt-2 flex-wrap" data-testid="chat-action-buttons">
                    <button 
                      size="sm" 
                      data-testid="deep-research-btn"
                      onClick={async () => {
                        const lastUserMsg = chatMessages.filter(m => m.role === 'user').slice(-1)[0];
                        const query = lastUserMsg ? lastUserMsg.content.substring(0, 100) : message.content.substring(0, 100);
                        setChatLoading(true);
                        try {
                          const response = await axios.post(`${BACKEND_URL}/api/dr-peptide/deep-research`, { query, max_results: 10, days_back: 730 }, { timeout: 60000 });
                          if (response.data.success) {
                            const results = response.data.results;
                            let researchSummary = `\nDeep Research Results (${results.total_results} studies found)\n\n`;
                            if (results.pubmed?.length > 0) {
                              researchSummary += `Recent PubMed Studies:\n`;
                              results.pubmed.slice(0, 5).forEach((study, i) => {
                                researchSummary += `${i+1}. ${study.authors} (${study.year}) - "${study.title}"\n   ${study.journal} | PMID: ${study.pmid}\n\n`;
                              });
                            }
                            if (results.trials?.length > 0) {
                              researchSummary += `\nActive Clinical Trials:\n`;
                              results.trials.forEach((trial, i) => {
                                researchSummary += `${i+1}. ${trial.title}\n   Status: ${trial.status} | Phase: ${trial.phase}\n\n`;
                              });
                            }
                            setChatMessages(prev => [...prev, { role: 'assistant', content: researchSummary }]);
                          }
                        } catch (err) {
                          toast.error('Deep research temporarily unavailable');
                        } finally {
                          setChatLoading(false);
                        }
                      }}
                      style={{ backgroundColor: '#0d9488', color: 'white' }}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium h-8 rounded-md px-3 text-xs border-none shadow-sm transition-colors hover:opacity-90"
                    >
                      <Search className="h-3.5 w-3.5" />
                      Deep Research
                    </button>
                    <Button variant="outline" size="sm" onClick={() => chatInputRef.current?.focus()}>Ask Another Question</Button>
                    <Button variant="outline" size="sm" onClick={() => chatWithDrPeptide("Can you clarify or expand on your last answer?")}>Ask About This Answer</Button>
                    <Button variant="outline" size="sm"
                      onClick={() => { setFeedbackContext(message.content.substring(0, 200)); setFeedbackText(''); setShowFeedbackModal(true); }}
                    >
                      Feedback
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(message.content); toast.success("Copied to clipboard"); }
                        catch { const textArea = document.createElement('textarea'); textArea.value = message.content; document.body.appendChild(textArea); textArea.select(); document.execCommand('copy'); document.body.removeChild(textArea); toast.success("Copied to clipboard"); }
                      }}
                    >
                      Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.print()}>Print</Button>
                    <Button variant="outline" size="sm"
                      onClick={async () => {
                        try {
                          if (navigator.share) { await navigator.share({ title: 'Dr. Peptide Response', text: message.content, url: window.location.href }); }
                          else { await navigator.clipboard.writeText(message.content); toast.success("Copied to clipboard"); }
                        } catch { try { await navigator.clipboard.writeText(message.content); toast.success("Copied"); } catch { toast.error("Unable to share"); } }
                      }}
                    >
                      Share
                    </Button>
                    <Button variant="outline" size="sm" disabled={generatingPatientSummary}
                      onClick={() => generatePatientSummary(message.content)}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                      data-testid="patient-summary-btn"
                    >
                      {generatingPatientSummary ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-1"></div> Generating...</>) : ('Patient Summary')}
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => {
                        setCreatePatientProtocol(message.content);
                        // Try to extract name from patient context
                        const nameMatch = (patientContext || '').match(/(?:name|patient)[:\s]+([A-Za-z]+ [A-Za-z]+)/i);
                        setCreatePatientName(nameMatch ? nameMatch[1] : '');
                        setShowCreatePatient(true);
                      }}
                      className="bg-green-600 text-white hover:bg-green-700"
                      data-testid="create-patient-from-chat-btn"
                    >
                      <FolderPlus className="h-3 w-3 mr-1" /> Create Patient File
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white border shadow-sm p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide - AI peptide therapy assistant" className="h-5 w-5 rounded-full" />
                    <span className="text-sm">Dr. Peptide is thinking...</span>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input — normal flow, always at bottom of card */}
          <div className={`border-t-2 border-slate-200 pt-4 bg-slate-50 rounded-b-lg px-4 pb-4 ${isDragging ? 'ring-2 ring-teal-400 bg-teal-50' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragging(false);
              const files = Array.from(e.dataTransfer.files);
              if (files.length > 0) {
                setUploadedLabFiles(prev => [...prev, ...files]);
                await handleFileUpload(files, 'labs');
              }
            }}
          >
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Ask Dr. Peptide
              </h3>
              <textarea
                ref={chatInputRef}
                placeholder="Enter your question for Dr. Peptide here"
                autoComplete="off"
                disabled={chatLoading}
                rows={3}
                data-testid="chat-input"
                className="w-full resize-none text-base leading-relaxed p-3 min-h-[60px] md:min-h-[300px] md:rows-12 border-2 focus:border-teal-500 shadow-lg rounded-md font-bold placeholder:font-bold"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !chatLoading) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <div className="flex items-center justify-between gap-2 md:gap-4">
                <div className="text-xs md:text-sm text-gray-500 px-1 md:px-2 hidden md:block">Press Enter to send - Shift+Enter for new line</div>
                <Button onClick={sendMessage} disabled={chatLoading} size="sm" className="px-4 py-2 md:px-12 md:py-6 text-sm md:text-lg ml-auto" data-testid="send-message-btn">
                  {chatLoading ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div> Sending...</>
                  ) : (
                    <><MessageCircle className="h-5 w-5 mr-2" /> Send Message</>
                  )}
                </Button>
              </div>
            </div>
            <div className="hidden md:block text-sm text-gray-600 mt-5 text-center px-6 py-4 bg-yellow-50 rounded-lg border-2 border-yellow-300 shadow-sm">
              <strong>Professional Use Only:</strong> Educational information only. Always consult healthcare provider for medical decisions.
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Disclaimer moved inside welcome message (Bug 3 fix) */}

      {/* Extraction Preview Modal */}
      {showExtractionModal && extractionPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">
                {extractionPreview.fallback ? 'Files Attached' : (extractionPreview.fileType === 'genetics' ? 'Genetic Analysis Complete!' : 'Lab Analysis Complete!')}
              </h2>
              <p className="text-gray-600 mt-2">
                {extractionPreview.fallback
                  ? 'Files attached successfully. Please review and add values manually below.'
                  : `Extracted data from ${extractionPreview.files.length} file(s). Review and confirm before adding to Patient Information.`}
              </p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {extractionPreview.files.map((file, idx) => (
                <div key={idx} className={`mb-6 rounded-lg p-4 border-2 ${
                  file.fileType === 'genetics' || extractionPreview.fileType === 'genetics' ? 'bg-purple-50 border-purple-200' : 'bg-teal-50 border-teal-200'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div>
                      <div className="font-semibold text-lg">{file.filename}</div>
                      {file.filesize && <div className="text-sm text-gray-500">{(file.filesize / 1024).toFixed(1)} KB</div>}
                    </div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-300">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {file.fileType === 'genetics' || extractionPreview.fileType === 'genetics' ? 'Genetic Markers Identified:' : 'Extracted Lab Data:'}
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {file.analysis?.extracted_text || file.analysis?.interpretation || JSON.stringify(file.analysis, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button onClick={applyExtractedData} className="flex-1" data-testid="add-to-patient-info-btn">Add to Patient Information</Button>
              <Button variant="outline" onClick={() => { setShowExtractionModal(false); setExtractionPreview(null); }}>Dismiss</Button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Summary Modal */}
      {showPatientSummary && patientSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-green-800">Patient-Friendly Summary</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPatientSummary(false)}>X</Button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="prose max-w-none">
                <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {typeof patientSummary.summary === 'string' ? patientSummary.summary : String(patientSummary.summary)}
                  </ReactMarkdown>
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500">Generated: {patientSummary.timestamp}</div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button className="flex-1" onClick={() => {
                const printWindow = window.open('', '_blank');
                printWindow.document.write('<html><head><title>Patient Summary</title><style>body{font-family:Arial,sans-serif;max-width:800px;margin:20px auto;padding:20px;line-height:1.6} h1{color:#166534;border-bottom:2px solid #166534;padding-bottom:10px} .footer{margin-top:40px;padding-top:20px;border-top:1px solid #ccc;font-size:12px;color:#666}</style></head><body>');
                printWindow.document.write(`<h1>Patient Summary</h1><div style="white-space:pre-wrap">${patientSummary.summary}</div><div class="footer"><p>Generated by PeptideProtocols.ai - ${patientSummary.timestamp}</p><p><em>This summary is for informational purposes. Always follow your healthcare provider\'s advice.</em></p></div></body></html>`);
                printWindow.document.close();
                printWindow.print();
              }}>
                Print Summary
              </Button>
              <Button variant="outline" onClick={async () => {
                try { await navigator.clipboard.writeText(patientSummary.summary); toast.success('Summary copied'); }
                catch { toast.error('Could not copy'); }
              }}>
                Copy
              </Button>
              <Button variant="outline" onClick={() => setShowPatientSummary(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Patient File Modal */}
      {showCreatePatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Create Patient File</h2>
            <p className="text-sm text-gray-600 mb-4">Save this conversation and protocol to a patient file for tracking.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name *</label>
                <input type="text" value={createPatientName} onChange={(e) => setCreatePatientName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter patient name" autoFocus data-testid="create-patient-name-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" disabled={!createPatientName.trim() || creatingPatient}
                  data-testid="confirm-create-patient-btn"
                  onClick={async () => {
                    if (!createPatientName.trim()) return;
                    setCreatingPatient(true);
                    try {
                      const code = (() => { try { return JSON.parse(localStorage.getItem("beta_access") || "{}").code || "DEMO"; } catch { return "DEMO"; } })();
                      const res = await axios.post(`${API}/patients`, {
                        practitioner_code: code,
                        patient_data: { patient_name: createPatientName.trim(), primary_concerns: ["From Dr. Peptide chat"] },
                        assessment: { primary_concerns: ["From Dr. Peptide chat"], patient_info_text: patientContext || "" },
                        protocol: { clinical_reasoning: createPatientProtocol, recommended_peptides: [], analysis: "Protocol from Dr. Peptide chat" }
                      }, { headers: getAuthHeaders() });
                      if (res.data.success) {
                        setShowCreatePatient(false);
                        setCreatePatientName('');
                        if (setCurrentView) setCurrentView('dashboard');
                      }
                    } catch (e) { console.error('Create patient error:', e); }
                    finally { setCreatingPatient(false); }
                  }}
                >
                  {creatingPatient ? 'Creating...' : 'Create Patient File'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreatePatient(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold mb-2">Provide Feedback</h2>
            <p className="text-sm text-gray-500 mb-4">Your feedback helps Dr. Peptide improve clinical accuracy.</p>
            <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[100px] focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Corrections, improvements, clinical observations..." autoFocus />
            <div className="flex gap-2 pt-3">
              <Button className="flex-1" disabled={!feedbackText.trim()}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/dr-peptide/feedback`, { message: feedbackContext, feedback: feedbackText.trim(), timestamp: new Date().toISOString() });
                    toast.success("Feedback submitted!");
                    setShowFeedbackModal(false);
                  } catch { toast.error("Could not submit feedback"); }
                }}>Submit Feedback</Button>
              <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrPeptideChat;
